<script setup lang="ts" generic="T">
import { type PopoverContentProps } from 'radix-vue'

import Button from '../Button/Button.vue'
import Popover from '../Popover/Popover.vue'
import { PopoverMenuOption } from './types'

type Props = {
  content?: PopoverContentProps & { class?: string }
  options: PopoverMenuOption<T>[]
  optionClass?: string
}
defineProps<Props>()

// emits
type Emits = {
  clickOption: [value: T]
}
const emit = defineEmits<Emits>()

// state
const isPopoverMenuOpen = defineModel<boolean>({ default: false })

// methods
function onClickOption(option: PopoverMenuOption<T>): void {
  if (option.onClick) {
    option.onClick()
  } else {
    emit('clickOption', option.value as T)
  }

  isPopoverMenuOpen.value = false
}
</script>

<template>
  <Popover
    v-model="isPopoverMenuOpen"
    unstyled
    :withArrow="false"
    :withCloseIcon="false"
    :content="{
      ...content,
      class: 'rounded-b border border-neutral-dark-0 bg-neutral-light-10',
    }"
    :trigger="{
      asChild: true,
    }"
  >
    <template #trigger>
      <slot name="trigger">
        <Button
          class="enabled:hover:bg-neutral-light-0 active:bg-neutral-light-5! rounded border-none disabled:cursor-default"
          :class="$attrs.class"
          square
          size="sm"
          icon="ellipsis-vertical"
          :disabled="!options.length"
        />
      </slot>
    </template>
    <template #content>
      <div
        v-if="options.length"
        class="flex flex-col py-2"
      >
        <Button
          v-for="option in options"
          :key="option.name"
          class="enabled:hover:bg-neutral-light-5 disabled:text-neutral-dark-10/50 p-2 text-left text-sm"
          :class="optionClass"
          :disabled="option.disabled"
          @click="onClickOption(option)"
        >
          <slot
            name="option"
            :option
          >
            {{ option.name }}
          </slot>
        </Button>
      </div>
    </template>
  </Popover>
</template>
